<script lang="ts">
	import { devices_status } from 'src/stores/device-runtime/status';
	//Hooks
	import { beforeUpdate } from 'svelte';
	import { format } from 'svelte-i18n';
	//Helpers
	import {
		get_gen_2_component_from_category,
		is_gen_2,
		is_gen_z,
		real_id,
		real_id_z,
	} from '../../../helpers/interface_helpers';
	import {
		localGroupRequest,
		handleCloudLocalLists,
		cloudGroupRequestV2,
		getDevicesFormatted,
	} from './control_group_helpers';
	//Stores
	import {
		createMemberStatusSnapshot,
		readDeviceStatus,
	} from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	//Design system components
	import {
		ActionsContainer,
		DeviceButton,
		ControlsContainer,
		toast,
		Alert,
		DeviceButtonV2,
		DashLightActions,
	} from '@Shelly/shelly-design-system';

	import type { Device } from '../../../types/Device';

	import type { Group } from '../../../types/Group';
	import { get_local_status } from 'src/services/local_requests';
	import { update_settings } from 'src/helpers/cloud_disabled_helpers';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { hasGroupControlRights } from 'src/helpers/user_helpers';

	export let variant:
		| 'listMenuCard'
		| 'widgetCard'
		| 'drawer'
		| 'dashControlWidget'
		| 'dashLightControl';
	export let group: Group;

	$: memberStatusStore = createMemberStatusSnapshot(group?.devices ?? []);
	$: memberStatus = $memberStatusStore;

	$: disabled =
		!hasGroupControlRights(group, $devices) ||
		group.devices.every((deviceId) => {
			const status = memberStatus?.[real_id(deviceId)];
			return !status?.online;
		});

	let cloud = {} as Device;
	let local = {} as Device;
	let turnState: string;
	let isLoading = { on: false, off: false };

	beforeUpdate(() => {
		const lists = handleCloudLocalLists(group.devices);
		[cloud, local] = [lists.cloud, lists.local];
	});

	const sendRequest = async (control) => {
		const isGenZ = Object.values(cloud).some((d) => is_gen_z($devices[real_id_z(d.id)]));
		turnState = control;
		isLoading[control] = true;

		if (Object.keys(cloud).length > 0) {
			const data = { on: control == 'on' ? true : false };
			let tmpDevices = getDevicesFormatted(Object.values(cloud), data);

			await cloudGroupRequestV2(
				`/v2/devices/set/groups`,
				tmpDevices,
				data,
				isGenZ ? 12000 : undefined,
				isGenZ ? false : true,
			);
		}

		if (Object.keys(local).length > 0) {
			for (const id in local) {
				const isGen2 = is_gen_2($devices[real_id(id)]);
				const isBreaker = group.type === 'breaker';

				const res = await localGroupRequest(
					local[id],
					isGen2 && !isBreaker
						? { on: control === 'on' }
						: isBreaker
							? { output: control === 'on' }
							: { turn: control },
					isGen2
						? `${get_gen_2_component_from_category($devices[real_id(id)])}.Set`
						: undefined,
					group.type,
				);

				if (res.isok) {
					const status_req = await get_local_status(
						$devices[real_id(id)],
						$devices[real_id(id)].ip,
					);
					let newStatus = {
						...readDeviceStatus($devices[real_id(id)].id),
						...status_req,
					};
					devices_status.update_device_status({
						id: $devices[real_id(id)].id,
						msg: newStatus,
					});
					await update_settings($devices[real_id(id)]);
				} else {
					toast.open({ type: 'danger', title: $format('error_global') });
				}
			}
		}
		turnState = undefined;
		isLoading[control] = false;
	};

	$: heading =
		group.devices.length === 0
			? $format('alert_heading_group_control_no_device', {
					default: 'Controls are unavailable. No devices are in the group.',
				})
			: $format('alert_heading_group_control_offline_removed_device', {
					default:
						'Controls are unavailable. All devices are currently offline or have been removed..',
				});
</script>

{#if variant === 'drawer'}
	{#if disabled}
		<Alert type="warning" class="mb-s" {heading} />
	{/if}
	{#if $showNewLayout}
		<ControlsContainer color="color-0">
			<DeviceButtonV2
				id="devicebutton-1yfnwqx"
				aria-label={$format('turn_on', { default: 'Turn On' })}
				slot="first"
				size="l"
				variant="success"
				isLoading={isLoading['on']}
				isActive={turnState === 'on'}
				inactiveIconColor="color-success"
				icon="fa fa-power-off"
				on:click={async (e) => {
					e.stopPropagation();
					await sendRequest('on');
				}}
				{disabled}
			/>
			<DeviceButtonV2
				aria-label={$format('turn_off', { default: 'Turn Off' })}
				id="devicebutton-vl34zyl"
				slot="second"
				size="l"
				variant="danger"
				isLoading={isLoading['off']}
				isActive={turnState === 'off'}
				inactiveIconColor="color-danger"
				icon="fa fa-power-off"
				on:click={async (e) => {
					e.stopPropagation();
					await sendRequest('off');
				}}
				{disabled}
			/>
		</ControlsContainer>
	{:else}
		<ControlsContainer color={$showNewLayout ? 'color-0' : undefined}>
			<DeviceButton
				id="devicebutton-1yfnwqx"
				aria-label={$format('turn_on', { default: 'Turn On' })}
				slot="first"
				size="l"
				variant="success"
				isLoading={isLoading['on']}
				isActive={turnState === 'on'}
				icon="fa fa-power-off"
				on:click={async (e) => {
					e.stopPropagation();
					await sendRequest('on');
				}}
				{disabled}
			/>
			<DeviceButton
				aria-label={$format('turn_off', { default: 'Turn Off' })}
				id="devicebutton-vl34zyl"
				slot="second"
				size="l"
				variant="danger"
				isLoading={isLoading['off']}
				isActive={turnState === 'off'}
				icon="fa fa-power-off"
				on:click={async (e) => {
					e.stopPropagation();
					await sendRequest('off');
				}}
				{disabled}
			/>
		</ControlsContainer>
	{/if}
{:else if variant === 'dashControlWidget'}
	<div class="d-flex gap-s">
		<DeviceButtonV2
			size="s"
			id="devicebutton-s2oh687"
			aria-label={$format('turn_on', { default: 'Turn On' })}
			isLoading={isLoading['on']}
			isActive={turnState === 'on'}
			inactiveIconColor="color-success"
			icon="fa-solid fa-power-off"
			variant="success"
			on:click={async (e) => {
				e.stopPropagation();
				await sendRequest('on');
			}}
			{disabled}
		/>

		<DeviceButtonV2
			size="s"
			aria-label={$format('turn_off', { default: 'Turn Off' })}
			id="devicebutton-5jou3c1"
			variant="danger"
			inactiveIconColor="color-danger"
			icon="fa-solid fa-power-off"
			isLoading={isLoading['off']}
			isActive={turnState === 'off'}
			on:click={async (e) => {
				e.stopPropagation();
				await sendRequest('off');
			}}
			{disabled}
		/>
	</div>
{:else if variant === 'dashLightControl'}
	<DashLightActions
		actionOptions={{
			topRightBtn: {
				icon: 'fa-regular fa-power-off',
				iconColor: '--color-success',
				loading: isLoading['on'],
			},
			bottomRightBtn: {
				icon: 'fa-regular fa-power-off',
				iconColor: '--color-danger',
				loading: isLoading['off'],
			},
		}}
		a11yProps={{
			topRightBtnAriaLabel: 'Button turn on',
			buttonsWrapperAriaLabel: 'Actions',
			bottomRightBtnAriaLabel: 'Button turn off',
		}}
		on:click-top-btn={async () => {
			await sendRequest('on');
		}}
		on:click-bottom-btn={async () => {
			await sendRequest('off');
		}}
	/>
{:else if variant === 'listMenuCard' && $showNewLayout}
	<ActionsContainer
		orientation="vertical"
		slot="right"
		bgColor="color-base"
		type="separator"
		separatorColor="color-2"
	>
		<DeviceButtonV2
			aria-label={$format('turn_on', { default: 'Turn On' })}
			id="devicebutton-s2oh687"
			variant="success"
			isLoading={isLoading['on']}
			isActive={turnState === 'on'}
			inactiveIconColor="color-success"
			slot="first"
			icon="fa fa-power-off"
			on:click={async (e) => {
				e.stopPropagation();
				await sendRequest('on');
			}}
			{disabled}
		/>
		<DeviceButtonV2
			aria-label={$format('turn_off', { default: 'Turn Off' })}
			id="devicebutton-5jou3c1"
			variant="danger"
			isLoading={isLoading['off']}
			isActive={turnState === 'off'}
			inactiveIconColor="color-danger"
			slot="second"
			icon="fa fa-power-off"
			on:click={async (e) => {
				e.stopPropagation();
				await sendRequest('off');
			}}
			{disabled}
		/>
	</ActionsContainer>
{:else}
	<ActionsContainer
		type={variant === 'widgetCard' ? 'simple' : 'separator'}
		orientation={variant === 'widgetCard' ? 'horizontal' : 'vertical'}
	>
		<DeviceButton
			aria-label={$format('turn_on', { default: 'Turn On' })}
			id="devicebutton-s2oh687"
			variant="success"
			isLoading={isLoading['on']}
			isActive={turnState === 'on'}
			slot="first"
			icon="fa fa-power-off"
			on:click={async (e) => {
				e.stopPropagation();
				await sendRequest('on');
			}}
			{disabled}
		/>
		<DeviceButton
			aria-label={$format('turn_off', { default: 'Turn Off' })}
			id="devicebutton-5jou3c1"
			variant="danger"
			isLoading={isLoading['off']}
			isActive={turnState === 'off'}
			slot="second"
			icon="fa fa-power-off"
			on:click={async (e) => {
				e.stopPropagation();
				await sendRequest('off');
			}}
			{disabled}
		/>
	</ActionsContainer>
{/if}
